import React, { Component, useState } from 'react';
import format from 'date-fns/format';
import Tracking from '@vromo/analytics';

import styled from '@emotion/styled';
import Color from 'color';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';

import ProfileImage from '@vromo/ui/components/ProfileImage';

import sumBy from 'lodash/sumBy';

import { Graph } from '@vromo/api/graph';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { useStore } from '@vromo/mobx-react/mobx-react';

const ETAContainer = styled.div`
    font-size: 16px;
    margin: 8px;
    color: ${({ theme }) => (Color(theme.background).isLight() ? 'black' : 'white')};
`;

const ETAStatus = styled.div`
    margin: 8px;
    color: ${({ theme }) => (Color(theme.background).isLight() ? 'black' : 'white')};
`;

const ETA = observer(function ETA({ job }) {
    function calculateETA(job) {
        const lastCompletion =
            job.progress === 0
                ? job?.underway?.created
                : job.tasks.items[job.progress - 1]?.completed?.time;

        return (
            (lastCompletion ?? new Date().valueOf()) +
            sumBy(job?.route?.slice(job.progress, job?.route?.length - 1), (it) => it?.duration)
        );
    }

    if (job.status === 'completed') {
        return (
            <ETAStatus>
                <span style={{ margin: '0 8px' }}>Completed</span>
                <FontAwesome icon="check" large />
            </ETAStatus>
        );
    }

    if (job.underway) {
        return (
            <ETAContainer id="__spatula_tracking_link_eta">
                ETA
                <br /> {format(calculateETA(job), 'hh:mm A')}
            </ETAContainer>
        );
    }

    return <ETAStatus>Calculating ETA...</ETAStatus>;
});

const Info = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-shrink: 0;
`;

const QuickInfo = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    flex-shrink: 0;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    margin: 8px;
    flex-shrink: 0;
`;

const Value = styled.div`
    font-weight: 400;
    font-size: 12px;
    color: ${({ theme }) => (Color(theme.background).isLight() ? 'black' : 'white')};
`;

const Button = styled.a`
    flex: 1;
    text-decoration: none;
    font-size: 11px;
    padding: 4px 10px;
    text-align: center;
    background: ${({ theme }) => theme.accent};
    border: none;
    border-radius: 10000px;
    color: ${({ theme }) => (Color(theme.accent).isLight() ? 'black' : 'white')};
    marginleft: 2px;
    margin-right: 2px;
    outline: none;
`;

export default observer(function DriverDetails({ auth, host }) {
    const [phone, setPhone] = useState('');
    const [sms, setSms] = useState('');

    const rootStore = useStore('rootStore');

    const job = rootStore.jobs.first();
    const vehicle = rootStore.vehicles.first();

    async function handleCallButtonClicked() {
        if (phone === '') {
            await new Graph(host).root
                .track(auth, job.id)
                .job()
                .contact()
                .channels()
                .post([{ methods: ['phone'] }])
                .then((response) => {
                    setPhone(response.items[0].phone);
                    window.open(`tel:${response.items[0].phone}`, '_blank');
                });
        } else {
            window.open(`tel:${phone}`, '_blank');
        }
    }

    async function handleSMSButtonClicked() {
        if (sms === '') {
            try {
                await new Graph(host).root
                    .track(auth, job.id)
                    .job()
                    .contact()
                    .channels()
                    .post([{ methods: ['sms'] }])
                    .then((response) => {
                        runInAction(() => {
                            setSms(response.items[0].phone);
                            window.open(`sms:${response.items[0].phone}`, '_blank');
                        });
                    });
            } catch (e) {
                alert('Sorry, you are unable to sms the driver at this point in time');
            }
        } else {
            window.open(`sms:${sms}`, '_blank');
        }
    }

    return (
        <Info>
            {vehicle ? (
                <ProfileImage url={vehicle.driver?.avatar?.url} user={vehicle?.driver} size={46} />
            ) : null}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <QuickInfo>
                    <Field>
                        <Value>{vehicle?.driver?.contact?.name}</Value>
                    </Field>
                    <ETA job={job} />
                </QuickInfo>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <Button onClick={handleCallButtonClicked}>Call</Button>
                    <Button onClick={handleSMSButtonClicked}>SMS</Button>
                </div>
            </div>
        </Info>
    );
});
